import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { TimelineSlider } from "./TimelineSlider";
import { StatisticsPanel } from "./StatisticsPanel";
import { GlobeVisualization } from "./GlobeVisualization";
import { getSignalPosts, getSignalMinMaxDate } from "../../../../api/signals";

export const SignalHome = ({
  darkmode,
  currentCase,
  currentPoint,
  sendToRightSidebarHome,
}) => {
  const [signalPoints, setSignalPoints] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().subtract(21, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState({
    severity: [],
    sources: [],
    countries: [],
  });

  useEffect(() => {
    getSignalMinMaxDate(currentCase.target).then(({ data: { start, end } }) => {
      setMinDate(dayjs.unix(start));
      setMaxDate(dayjs.unix(end));
    });
  }, [currentCase?.target]);

  useEffect(() => {
    if (!currentCase?.target || currentCase?.case_type !== "signal") {
      setLoading(false);
      return;
    }

    setLoading(true);
    getSignalPosts(currentCase.target, {
      start: startDate.unix(),
      end: endDate.unix(),
    })
      .then(({ data }) => {
        const transformed = data.map((point) => ({
          ...point,
          id: point.document_id,
          lat: point.latitude,
          lon: point.longitude,
          date: dayjs.unix(point.timestamp).format("YYYY-MM-DD"),
          severity:
            point.severity >= 0.88
              ? "high"
              : point.severity >= 0.66
                ? "medium"
                : point.severity > 0
                  ? "low"
                  : null,
          screenshots: point.screenshot
            ? [
                { id: `${point.document_id}-1`, url: point.screenshot },
                ...(point.screenshot_full
                  ? [
                      {
                        id: `${point.document_id}-2`,
                        url: point.screenshot_full,
                      },
                    ]
                  : []),
              ]
            : [],
        }));
        setSignalPoints(transformed);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching signal posts:", error);
        setSignalPoints([]);
        setLoading(false);
      });
  }, [currentCase?.target]);

  const filteredData = useMemo(() => {
    if (!startDate || !endDate) return [];
    const startTimestamp = startDate.unix();
    const endTimestamp = endDate.unix();

    return signalPoints.filter((item) => {
      const inDateRange =
        item.timestamp >= startTimestamp && item.timestamp <= endTimestamp;
      if (!inDateRange) return false;
      if (
        activeFilters.severity.length > 0 &&
        !activeFilters.severity.includes(item.severity)
      )
        return false;
      if (
        activeFilters.sources.length > 0 &&
        !activeFilters.sources.includes(item.source)
      )
        return false;
      if (
        activeFilters.countries.length > 0 &&
        !activeFilters.countries.includes(item.country)
      )
        return false;
      return true;
    });
  }, [signalPoints, startDate, endDate, activeFilters]);

  const sortedPoints = useMemo(() => {
    return [...filteredData].sort((a, b) => a.timestamp - b.timestamp);
  }, [filteredData]);

  const handleDateRangeChange = (newStartDate, newEndDate) => {
    setStartDate(dayjs(newStartDate));
    setEndDate(dayjs(newEndDate));
  };

  const handlePointClick = (point) => {
    sendToRightSidebarHome(point);
  };

  const handlePointNavigate = (point) => {
    sendToRightSidebarHome(point);
  };

  const handleFilterToggle = (filterType, value) => {
    setActiveFilters((prev) => {
      const currentFilters = prev[filterType];
      const isActive = currentFilters.includes(value);
      return {
        ...prev,
        [filterType]: isActive
          ? currentFilters.filter((item) => item !== value)
          : [...currentFilters, value],
      };
    });
  };

  const containerClasses = [
    "relative flex w-full h-full overflow-hidden",
    "bg-slate-50 dark:bg-[#0B0F14]",
  ].join(" ");

  if (loading) return null;

  if (signalPoints.length === 0) {
    return (
      <div className={`${containerClasses} items-center justify-center`}>
        <div className="text-slate-500 dark:text-gray-400 text-sm uppercase tracking-wide">
          No signal data available
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      {/* Globe fills the entire container behind everything */}
      <div className="absolute inset-0 z-0">
        <GlobeVisualization
          data={filteredData}
          onPointClick={handlePointClick}
          currentPoint={currentPoint}
          darkmode={darkmode}
        />
      </div>

      {/* Statistics panel floats on top */}
      <div
        className="relative z-10 w-72 shrink-0 p-6 pt-20 max-h-full overflow-y-auto"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(148, 163, 184, 0.5) transparent",
        }}
      >
        <StatisticsPanel
          totalIncidents={signalPoints.length}
          filteredCount={filteredData.length}
          startDate={startDate?.toDate()}
          endDate={endDate?.toDate()}
          filteredData={filteredData}
          allData={signalPoints}
          activeFilters={activeFilters}
          onFilterToggle={handleFilterToggle}
        />
      </div>

      {/* Timeline floats on top */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center z-20">
        <div className="w-2/3 max-w-2xl">
          <TimelineSlider
            startDate={startDate?.toDate()}
            endDate={endDate?.toDate()}
            onDateRangeChange={handleDateRangeChange}
            minDate={minDate?.toDate()}
            maxDate={maxDate?.toDate()}
            sortedPoints={sortedPoints}
            allPoints={signalPoints}
            currentPoint={currentPoint}
            onPointNavigate={handlePointNavigate}
          />
        </div>
      </div>
    </div>
  );
};

export default SignalHome;
